import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FolderKanban, Plus, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Projekte');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, formData);
        toast.success('Projekt aktualisiert!');
      } else {
        await axios.post(`${API}/projects`, formData);
        toast.success('Projekt erstellt!');
      }
      
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Möchten Sie dieses Projekt wirklich löschen?')) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('Projekt gelöscht!');
      fetchProjects();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setEditingProject(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="projects-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Projekte</h1>
            <p className="text-gray-600 mt-2">Verwalten Sie Ihre Projekte</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="create-project-button">
                <Plus className="w-4 h-4 mr-2" />
                Neues Projekt
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="project-dialog">
              <DialogHeader>
                <DialogTitle>{editingProject ? 'Projekt bearbeiten' : 'Neues Projekt erstellen'}</DialogTitle>
                <DialogDescription>
                  {editingProject ? 'Aktualisieren Sie die Projektinformationen' : 'Erstellen Sie ein neues Projekt für die Zeiterfassung'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Projektname *</Label>
                  <Input
                    id="name"
                    placeholder="z.B. Website Redesign"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    placeholder="Kurze Beschreibung des Projekts..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="project-description-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={loading} data-testid="save-project-button">
                    {loading ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading && projects.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="card-hover border-l-4 border-l-blue-500" data-testid={`project-card-${project.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FolderKanban className="w-5 h-5 text-blue-600" />
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(project)}
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(project.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {project.description ? (
                    <p className="text-sm text-gray-600 mb-3">{project.description}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic mb-3">Keine Beschreibung</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Erstellt am {format(new Date(project.created_at), 'dd.MM.yyyy', { locale: de })}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <FolderKanban className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Noch keine Projekte</h3>
              <p className="text-gray-500 mb-4">Erstellen Sie Ihr erstes Projekt für die Zeiterfassung</p>
              <Button onClick={handleOpenDialog} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Projekt erstellen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Projects;
